import { usePersistedState } from "@/lib/persistedFilters";
// Iter 91aj — Humi Time Off.
//   [Mine] [Team]                    │  [Request time off] button
//   Table of my/team PTO requests    │
//   Approve/deny controls for manager rows

import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Plus, CheckCircle2, XCircle, Loader2, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";

const KINDS = [
  { value: "vacation", label: "Vacation" },
  { value: "sick", label: "Sick" },
  { value: "personal", label: "Personal" },
  { value: "unpaid", label: "Unpaid" },
];

const STATUS_STYLES = {
  pending:  "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  approved: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  denied:   "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
};

export default function HumiTimeOff() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [team, setTeam] = usePersistedState("humi-timeoff.team", []);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [form, setForm] = useState({ kind: "vacation", start_date: "", end_date: "", reason: "" });
  const [decideTarget, setDecideTarget] = useState(null);
  const [decision, setDecision] = useState({ status: "approved", decided_reason: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/humi/pto", { params: { scope: "mine" } }),
        api.get("/humi/pto", { params: { scope: "team" } }),
      ]);
      setMine(m.data.items || []);
      setTeam(t.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const submitRequest = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Please pick a start and end date."); return; }
    try {
      await api.post("/humi/pto", form);
      toast.success("Time off request submitted.");
      setReqOpen(false); setForm({ kind: "vacation", start_date: "", end_date: "", reason: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decide = async () => {
    if (!decideTarget) return;
    try {
      await api.patch(`/humi/pto/${decideTarget.request_id}`, decision);
      toast.success(`Request ${decision.status}.`);
      setDecideTarget(null); setDecision({ status: "approved", decided_reason: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancel = async (rid) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await api.delete(`/humi/pto/${rid}`);
      toast.success("Request cancelled.");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const teamPending = useMemo(() => team.filter((r) => r.status === "pending" && r.user_id !== user?.user_id), [team, user]);

  return (
    <div className="space-y-4" data-testid="humi-time-off">
      <PageHeader
        icon={<CalendarClock className="h-5 w-5 text-primary" />}
        title="Time off"
        subtitle="Request PTO and manage your team's approvals."
      >
        <Button data-testid="humi-request-pto" onClick={() => setReqOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground" size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Request time off
        </Button>
      </PageHeader>

      <Card className="p-0 border-slate-200 dark:border-slate-700 overflow-hidden">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="border-b border-slate-100 dark:border-slate-800 px-4">
            <TabsList className="bg-transparent gap-4">
              <TabsTrigger value="mine" data-testid="humi-pto-tab-mine" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
                My requests <span className="ml-2 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300">{mine.length}</span>
              </TabsTrigger>
              <TabsTrigger value="team" data-testid="humi-pto-tab-team" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
                Team {teamPending.length > 0 && (
                  <span className="ml-2 rounded-full bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">{teamPending.length} pending</span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="mine" className="mt-0">
            <PtoTable
              rows={mine}
              loading={loading}
              currentUserId={user?.user_id}
              onCancel={cancel}
              emptyMsg="You haven't requested any time off yet."
              showActionsForOwn
            />
          </TabsContent>

          <TabsContent value="team" className="mt-0">
            <PtoTable
              rows={team}
              loading={loading}
              currentUserId={user?.user_id}
              onDecide={setDecideTarget}
              emptyMsg="No team requests in your scope."
            />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Request PTO dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request time off</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="humi-pto-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start</Label>
                <Input data-testid="humi-pto-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>End</Label>
                <Input data-testid="humi-pto-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reason (optional)</Label>
              <Textarea data-testid="humi-pto-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Family trip, appointment, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
            <Button data-testid="humi-pto-submit" onClick={submitRequest} className="bg-primary hover:bg-primary/90 text-primary-foreground">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decide dialog */}
      <Dialog open={!!decideTarget} onOpenChange={(o) => !o && setDecideTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Review request</DialogTitle></DialogHeader>
          {decideTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm">
                <div><span className="text-slate-500">Employee:</span> <span className="font-medium">{decideTarget.user_name}</span></div>
                <div><span className="text-slate-500">Type:</span> {decideTarget.kind}</div>
                <div><span className="text-slate-500">Dates:</span> {decideTarget.start_date} → {decideTarget.end_date} <span className="text-slate-400">({decideTarget.days_count}d)</span></div>
                {decideTarget.reason && <div className="mt-1 text-xs text-slate-500">“{decideTarget.reason}”</div>}
              </div>
              <div className="space-y-1.5">
                <Label>Decision</Label>
                <Select value={decision.status} onValueChange={(v) => setDecision({ ...decision, status: v })}>
                  <SelectTrigger data-testid="humi-pto-decision"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approve</SelectItem>
                    <SelectItem value="denied">Deny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Textarea data-testid="humi-pto-decided-reason" rows={2} value={decision.decided_reason} onChange={(e) => setDecision({ ...decision, decided_reason: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideTarget(null)}>Cancel</Button>
            <Button data-testid="humi-pto-decide-save" onClick={decide} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PtoTable({ rows, loading, currentUserId, onDecide, onCancel, emptyMsg, showActionsForOwn }) {
  if (loading) {
    return <div className="flex items-center justify-center p-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }
  if (!rows.length) {
    return <div className="p-10 text-center text-sm text-slate-400" data-testid="humi-pto-empty">{emptyMsg}</div>;
  }
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
      {rows.map((r) => (
        <li key={r.request_id} data-testid={`humi-pto-row-${r.request_id}`}
            className="flex flex-wrap items-center gap-3 px-4 py-3">
          <Avatar name={r.user_name} src={r.user_avatar_url} className="h-9 w-9 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-800 dark:text-slate-100">{r.user_name || "—"}</span>
              <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600 dark:text-slate-300">{r.kind}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600"}`} data-testid={`humi-pto-status-${r.request_id}`}>{r.status}</span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {r.start_date} → {r.end_date} <span className="text-slate-400">· {r.days_count} day{r.days_count === 1 ? "" : "s"}</span>
            </div>
            {r.reason && <div className="mt-0.5 text-xs italic text-slate-500 dark:text-slate-400">“{r.reason}”</div>}
            {r.decided_reason && <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">Manager note: {r.decided_reason}</div>}
          </div>
          <div className="flex items-center gap-2">
            {onDecide && r.status === "pending" && r.user_id !== currentUserId && (
              <Button size="sm" variant="outline" data-testid={`humi-pto-review-${r.request_id}`} onClick={() => onDecide(r)}>
                Review
              </Button>
            )}
            {showActionsForOwn && r.status !== "approved" && (
              <Button size="icon" variant="ghost" title="Cancel" data-testid={`humi-pto-cancel-${r.request_id}`} onClick={() => onCancel(r.request_id)}>
                <Trash2 className="h-4 w-4 text-slate-400" />
              </Button>
            )}
            {r.status === "approved" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            {r.status === "denied" && <XCircle className="h-4 w-4 text-red-500" />}
          </div>
        </li>
      ))}
    </ul>
  );
}
